import React, { ReactElement } from 'react'

import { ApiKeyScope, ApiKeyScopes } from '../../model/apiKey'
import { ApiKeyReducer } from '../../reducers/apiKeys'
import { FormComponentProps, withValidations } from '../../utils/form-utils'
import { formatApiKeyScope } from '../../utils/format-utils'
import { setByPath } from '../../utils/object-utils'
import { t, tx } from '../../utils/translation-utils'
import Button from '../elements/button'
import Checkbox from '../elements/checkbox'
import Col from '../elements/grid/col'
import Row from '../elements/grid/row'
import Input from '../elements/input'
import SwitchWrapper from '../form-elements/SwitchWrapper'
import LoadingOverlay from '../widgets/LoadingOverlay'

type Props = {
  apiKeyID?: string
  apiKeys: ApiKeyReducer
}

type Fields = {
  description?: string
  securityAcknowledged?: boolean
} & Record<`toggle${ApiKeyScope}`, boolean>

export type ApiKeyResult = {
  description: string
  scopes: ApiKeyScope[]
}

function ApiKeyEditForm(props: Props & FormComponentProps<Fields, ApiKeyResult>): ReactElement | null {
  const { decorateField } = props

  return (
    <div style={{ fontSize: '14px' }}>
      {props.getFormError()}
      <Row>
        <Col span={24}>
          {decorateField('description', {
            placeholder: t('api_key.edit.form.description'),
            validate: (val) => (!val ? t('api_key.edit.form.validation.description_required') : null),
          })(<Input style={{ width: '100%' }} />)}
        </Col>
      </Row>
      <Row>
        {ApiKeyScopes.map((scope, i) => (
          <Col span={12} key={`scope-${i}`}>
            <SwitchWrapper<Fields> id={`toggle${scope}`} decorateField={decorateField}>
              {formatApiKeyScope(scope)}
            </SwitchWrapper>
          </Col>
        ))}
      </Row>
      {!props.apiKeyID && (
        <Row style={{ marginTop: '24px' }}>
          <Col span={24}>
            <p style={{ fontSize: '14px', marginBottom: '8px' }}>
              {tx('api_key.edit.form.docs_link', {
                link: (
                  <a
                    href="https://help.salary.dk/da/articles/14414697-salary-api-faq"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('api_key.edit.form.docs_link_text')}
                  </a>
                ),
              })}
            </p>
            {decorateField('securityAcknowledged', {
              valueOnChecked: true,
              noBlur: true,
              skipLabel: true,
              validate: (val) => (!val ? t('api_key.edit.form.validation.security_warning_required') : null),
            })(
              <Checkbox>
                <span style={{ fontSize: '14px', paddingLeft: '2px' }}>{t('api_key.edit.form.security_warning')}</span>
              </Checkbox>
            )}
          </Col>
        </Row>
      )}
      <Row style={{ marginTop: '16px' }}>
        <Col span={24}>
          <Button htmlType="submit" size="extra-extra-large" type="primary">
            {t('form.button.save_changes')}
          </Button>
        </Col>
      </Row>
      {props.apiKeys.saving && <LoadingOverlay />}
    </div>
  )
}

export default withValidations<Props, Fields, ApiKeyResult>({
  mapPropsToFields: (props) => {
    const fields: Fields = {
      toggleReadAll: true,
      toggleWriteAll: true,
      toggleReadBaseData: false,
      toggleReadEmployments: false,
      toggleReadLeave: false,
      toggleReadPersonalData: false,
      securityAcknowledged: false,
    }
    const apiKey = props.apiKeys.apiKeys.find((apiKey) => apiKey.id === props.apiKeyID)
    if (apiKey) {
      fields.description = apiKey.description
      fields.toggleReadAll = apiKey.scopes.some((scope) => scope === 'ReadAll')
      fields.toggleWriteAll = apiKey.scopes.some((scope) => scope === 'WriteAll')
      fields.toggleReadBaseData = apiKey.scopes.some((scope) => scope === 'ReadBaseData')
      fields.toggleReadEmployments = apiKey.scopes.some((scope) => scope === 'ReadEmployments')
      fields.toggleReadLeave = apiKey.scopes.some((scope) => scope === 'ReadLeave')
      fields.toggleReadPersonalData = apiKey.scopes.some((scope) => scope === 'ReadPersonalData')
    }
    return fields
  },
  onChange: (key, val) => {
    const values: Partial<Fields> = {}
    setByPath(values, key, val)
    if (key === 'toggleWriteAll' && val === true) {
      setByPath(values, 'toggleReadAll', true) // force read all to be true, when write all is true
    }
    return values
  },
  onSubmit: (values) => ({
    description: values.description!,
    scopes: ApiKeyScopes.filter((scope) => values[`toggle${scope}`]),
  }),
})(ApiKeyEditForm)
